import { Component } from 'react';

import OhNo from '@imgs/custom/caution2.webp';
import * as Sentry from '@sentry/react';
import {
  Box,
  Text,
  Button,
  Paper,
  AppShell,
  Alert,
  Stack,
  Center,
  Group
} from '@ui';

import config, { APP_STAGE_DEVELOPMENT, APP_STAGE_STAGING } from '../config';

import classes from './styles/ErrorBoundary.module.css';

interface ErrorBoundaryState {
  hasError: boolean;
  error: any;
}

class MantineErrorBoundary extends Component<any, ErrorBoundaryState> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    // Log the error to Sentry
    if (
      config.APP_STAGE === APP_STAGE_DEVELOPMENT ||
      config.APP_STAGE === APP_STAGE_STAGING
    ) {
      //don't remove
      console.log({ error, errorInfo });
    }
    // prevent reporting chunk load error
    if (error.name !== 'ChunkLoadError') {
      Sentry.withScope((scope) => {
        scope.setExtras(errorInfo);
        Sentry.captureException(error);
      });
    }
  }

  render() {
    const { error, hasError } = this.state;

    const showErrorInfo = config.APP_STAGE === APP_STAGE_DEVELOPMENT;

    if (hasError) {
      return (
        // temporary layout till we create custom layout
        <AppShell>
          <AppShell.Main>
            <Center mih="100dvh" py="xl">
              <Stack
                justify="center"
                align="center"
                h={!showErrorInfo ? '50vh' : 'auto'}
              >
                <Stack maw={450} dir="column" align="center">
                  {error.name === 'ChunkLoadError' ? (
                    <>
                      <Alert color="info">
                        This application has been updated, please refresh your
                        browser to see the latest content.
                      </Alert>
                      <Box w={250}>
                        <Button
                          variant="filled"
                          color="primary"
                          fullWidth
                          onClick={() => window.location.reload()}
                        >
                          Update
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Stack
                        display="flex"
                        dir="column"
                        align="center"
                        justify="center"
                      >
                        <Paper
                          shadow="xs"
                          p="md"
                          maw={320}
                          ta="center"
                          bg="secondary.1"
                        >
                          <img
                            alt="An unexpected error occurred"
                            height={86}
                            width={66}
                            src={OhNo}
                          />
                          <Stack display="flex" gap="xs">
                            <Text c="danger" fw={600}>
                              An error has occurred
                            </Text>
                            <Text c="dimmed">Please refresh and try again.</Text>
                            <Box mt={16}>
                              <Button
                                variant="filled"
                                color="primary"
                                fullWidth
                                onClick={() => window.location.reload()}
                              >
                                Refresh
                              </Button>
                            </Box>
                          </Stack>
                        </Paper>
                        {showErrorInfo && (
                          <Paper
                            shadow="xs"
                            p="md"
                            className={classes.errorContainer}
                            my="xl"
                          >
                            <Text size="lg" className={classes.errorText}>
                              Error Info
                            </Text>
                            <Group mt={8}>
                              <Text size="md">
                                <strong>Name:</strong> {error?.name || 'N/A'}
                              </Text>
                              <Text size="md">
                                <strong>Message:</strong> {error?.message || 'N/A'}
                              </Text>
                            </Group>
                            <Box mt={16} className={classes.scrollBox}>
                              <Text
                                component="pre"
                                size="sm"
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: 'monospace'
                                }}
                              >
                                {error?.stack || 'No stack trace available'}
                              </Text>
                            </Box>
                          </Paper>
                        )}
                      </Stack>
                    </>
                  )}
                </Stack>
              </Stack>
            </Center>
          </AppShell.Main>
        </AppShell>
      );
    }
    return this.props.children;
  }
}

export default MantineErrorBoundary;
